import * as React from 'react';

/**
 * Hook for two-stage filtering: external filter + search filter.
 * Consolidates the filtering pattern used across all entity select components.
 *
 * @param items - The array of items to filter (from a data hook)
 * @param externalFilter - Optional filter function to apply first (e.g., exclude already selected items)
 * @param searchFilter - Search filter function to apply second (text-based search)
 * @returns Filtered array of items
 */
export function useFilteredItems<T>(
  items: T[] | undefined,
  externalFilter: ((item: T) => boolean) | undefined,
  searchFilter: (item: T) => boolean
): T[] {
  return React.useMemo(() => {
    if (!items) return [];

    // Stage 1: Apply external filter if provided
    let result = items;
    if (externalFilter) {
      result = result.filter(externalFilter);
    }

    // Stage 2: Apply search filter
    return result.filter(searchFilter);
  }, [items, externalFilter, searchFilter]);
}
